import { Section } from '@/components/shared';
import { Grid, GridItem, Heading, Image, Text } from '@chakra-ui/react';
import { useKeenSlider } from 'keen-slider/react';
import { useTranslation } from 'react-i18next';
import { packaging } from '../constants';
import { useTrackCurrentSlide, useTrackLoadedState } from '../hooks';
import type { ISectionItemWithImage } from '../types';
import { SlideDots } from './SlideDots';

const PackagingItem: React.FC<Omit<ISectionItemWithImage, 'content'>> = (props): JSX.Element => {
  const { image, srcSet, title } = props;
  return (
    <GridItem
      className="keen-slider__slide"
      color="white"
      maxW={{ base: 250, md: 'full' }}
      minW={{ base: 250, md: 'full' }}
      position="relative"
      w="full"
    >
      <Image bg="gray.200" h={{ base: 250, md: 300 }} src={image} srcSet={srcSet} w="full" />
      <Heading
        as="h5"
        bottom={0}
        fontSize={{ base: 'large', md: 'h3.base' }}
        fontWeight="bold"
        left={0}
        maxW={{ base: 200, md: 250 }}
        p={4}
        pos="absolute"
        textTransform="uppercase"
        zIndex={1}
      >
        {title}
      </Heading>
    </GridItem>
  );
};

export const Packaging: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useTrackCurrentSlide();
  const [isLoaded, setLoaded] = useTrackLoadedState();

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    disabled: false,
    mode: 'free-snap',
    slides: {
      perView: 'auto',
      origin: 'auto',
      spacing: 18,
    },
    breakpoints: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      '(min-width: 48em)': {
        disabled: true,
      },
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    },
  });

  return (
    <Section
      headingProps={{ mt: { base: 12, md: '120px' } }}
      id="packaging"
      title={t('LANDING.PACKAGING.SECTION_TITLE')}
    >
      <Text fontSize={{ base: 'medium', md: 'large' }} textAlign="center">
        {t('LANDING.PACKAGING.SECTION_CONTENT')}
      </Text>
      <Grid
        className="keen-slider"
        gap={{ base: 0, md: 6 }}
        ref={sliderRef}
        templateColumns={{ md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
        w="full"
      >
        {packaging.map(({ title, ...rest }) => (
          <PackagingItem key={rest.id} title={t(title)} {...rest} />
        ))}
      </Grid>
      {isLoaded && instanceRef.current && (
        <SlideDots currentSlide={currentSlide} slider={instanceRef} />
      )}
    </Section>
  );
};
